import React, { RefObject } from 'react';

import { Button } from '@reckon-web/button';
import { Stack } from '@reckon-web/stack';
import { Text } from '@reckon-web/text';
import { Heading } from '@reckon-web/heading';
import { Divider } from '@reckon-web/divider';
import { Notice } from '@reckon-web/notice';
import { Form } from '@reckon-web/forms';
import { useTheme } from '@reckon-web/theme';
import { Flex } from '@reckon-web/flex';

import { TextField } from '../../generic/TextField';
import { WidgetContainer } from '../../generic/wigetContainer';

import { formSchema } from './formSchema';
import { Step, SubmitError } from './types';
import { SingleSignOnButtons } from './singleSignOnButtons';

type SignInProps = {
  form: Form<ReturnType<typeof formSchema>>;
  submitAction: {
    onClick: () => void;
    error?: SubmitError;
    loading: boolean;
    disabled?: boolean;
  };
  step: Step;
  SingleSignOnActions?: {
    microsoft?: { onClick: () => void };
    google?: { onClick: () => void };
    apple?: { onClick: () => void };
  };
  isProductProvided: boolean;
  navigation?: {
    navigateToCreateAccount?: () => void;
    navigateToForgotPassword?: () => void;
  };
  resetPasswordSuccess: boolean;
  passwordInputRef: RefObject<HTMLInputElement | null>;
};

export const SignInForm = ({
  form,
  submitAction,
  step,
  SingleSignOnActions,
  navigation,
  resetPasswordSuccess,
  passwordInputRef,
  isProductProvided,
}: SignInProps) => {
  const { spacing } = useTheme();

  return (
    <WidgetContainer>
      <Flex direction="column" margin="xxlarge" gap="xlarge">
        <>
          <Heading align="center" level="5">
            Sign in to {isProductProvided ? 'continue' : 'your Reckon account'}
          </Heading>

          {submitAction.error && (
            <Notice
              actions={submitAction.error.actions}
              tone={submitAction.error.tone}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: submitAction.error.description,
                }}
              />
            </Notice>
          )}

          {resetPasswordSuccess && (
            <Notice tone="positive">
              <Text>
                Password has been reset. Please sign in with your new password.
              </Text>
            </Notice>
          )}

          <Flex
            as="form"
            direction="column"
            gap="large"
            marginTop="xlarge"
            onSubmit={submitAction.onClick}
          >
            <TextField
              label="Email address or username"
              placeholder="Enter email or username"
              {...form.fields.emailOrUsername.props}
            />

            {step === 'login' && (
              <TextField
                ref={passwordInputRef}
                label="Password"
                type="password"
                placeholder="Enter password"
                {...form.fields.password.props}
              />
            )}

            <Button
              type="submit"
              label="Continue"
              loading={submitAction.loading}
              block
              disabled={submitAction?.disabled}
            />
          </Flex>
        </>

        {SingleSignOnActions && (
          <Flex direction="column" gap="xlarge">
            <Text align="center">- OR -</Text>
            <SingleSignOnButtons {...SingleSignOnActions} />
          </Flex>
        )}

        {navigation && (
          <>
            <Divider />
            <Stack marginTop="medium">
              {navigation.navigateToCreateAccount && (
                <Text align="center">
                  New to Reckon?
                  <Button
                    label="Create a new account"
                    weight="none"
                    onClick={navigation.navigateToCreateAccount}
                    style={{ padding: spacing.small }} // FIXME: GAP?
                  />
                </Text>
              )}

              {navigation.navigateToForgotPassword && (
                <Button
                  label="Forgotten password"
                  weight="none"
                  onClick={navigation.navigateToForgotPassword}
                  block
                />
              )}
            </Stack>
          </>
        )}
      </Flex>
    </WidgetContainer>
  );
};
